import { Injectable } from '@angular/core';

const NUMBER_OF_ROWS_HEAT_MAP = 10;
const NUMBER_OF_COLUMS_HEAT_MAP = 10;

@Injectable({
  providedIn: 'root',
})
export class HeatMapMetricsService {
  private m = NUMBER_OF_ROWS_HEAT_MAP; // Number of rows
  private n = NUMBER_OF_COLUMS_HEAT_MAP; // Number of columns
  private gridMatrix: number[][] = Array.from({ length: this.m }, () =>
    Array(this.n).fill(0),
  );
  private toolPaths: {
    toolLeft: [number, number][];
    toolRight: [number, number][];
  } = { toolLeft: [], toolRight: [] };
  private totalDistance: { toolLeft: number; toolRight: number } = {
    toolLeft: 0,
    toolRight: 0,
  };

  /** Updates the heatmap with new tool coordinates */
  updateToolPosition(
    x: number,
    y: number,
    tool: 'toolLeft' | 'toolRight',
    visualize: boolean,
    ctx?: CanvasRenderingContext2D,
  ): void {
    if (ctx) {
      this.detectCell(x, y, tool, ctx);
    }

    if (visualize && ctx) {
      this.visualizeHeatMap(ctx);
    }
  }

  /** Detects the cell for an object and updates occurrences */
  private detectCell(
    x: number,
    y: number,
    tool: 'toolLeft' | 'toolRight',
    ctx?: CanvasRenderingContext2D,
  ): void {
    const cellWidth = ctx.canvas.width / NUMBER_OF_COLUMS_HEAT_MAP;
    const cellHeight = ctx.canvas.height / NUMBER_OF_ROWS_HEAT_MAP;

    const col = Math.floor(x / cellWidth);
    const row = Math.floor(y / cellHeight);
    if (row >= 0 && row < this.m && col >= 0 && col < this.n) {
      this.gridMatrix[row][col] += 1;
      // Store cell coordinates
      this.toolPaths[tool].push([row, col]);

      // Calculate and accumulate total distance
      if (this.toolPaths[tool].length > 1) {
        const [prevRow, prevCol] =
          this.toolPaths[tool][this.toolPaths[tool].length - 2];
        const distance = Math.sqrt(
          Math.pow(row - prevRow, 2) + Math.pow(col - prevCol, 2),
        );
        this.totalDistance[tool] += distance;
      }
    }
  }

  /** Visualizes the heatmap on a given canvas context */
  visualizeHeatMap(ctx: CanvasRenderingContext2D): void {
    const cellWidth = ctx.canvas.width / this.n;
    const cellHeight = ctx.canvas.height / this.m;
    const maxOccurrences = Math.max(...this.gridMatrix.flat());

    for (let row = 0; row < this.m; row++) {
      for (let col = 0; col < this.n; col++) {
        const color = this.numberToColor(
          this.gridMatrix[row][col],
          maxOccurrences,
        );
        ctx.globalAlpha = 0.3;
        ctx.fillStyle = color;
        ctx.fillRect(col * cellWidth, row * cellHeight, cellWidth, cellHeight);

        ctx.strokeStyle = '#000';
        ctx.strokeRect(
          col * cellWidth,
          row * cellHeight,
          cellWidth,
          cellHeight,
        );
      }
    }
  }

  numberToColor(value: number, maxValue: number): string {
    if (maxValue === 0) return 'rgb(0, 0, 255)'; // Default to blue for empty cells

    const normalized = value / maxValue; // Normalize between 0 and 1
    const r = Math.min(255, Math.floor(255 * normalized)); // Red increases with value
    const g = Math.min(255, Math.floor(255 * (1 - normalized))); // Green decreases
    const b = Math.floor(255 * (1 - normalized * 0.5)); // Keep some blue

    return `rgb(${r},${g},${b})`;
  }

  /** Calculates mean density of movements in grid */
  meanDensity(): number {
    const total = this.gridMatrix.flat().reduce((sum, v) => sum + v, 0);
    return total / (this.m * this.n);
  }

  /** Calculates variance of movements */
  variance(): number {
    const mean = this.meanDensity();
    const values = this.gridMatrix.flat();
    return (
      values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / values.length
    );
  }

  /** Standard deviation derived from variance */
  standardDeviation(): number {
    return Math.sqrt(this.variance());
  }

  /** Measures entropy in movement distribution */
  entropy(): number {
    const total = this.gridMatrix.flat().reduce((sum, v) => sum + v, 0);
    if (total === 0) return 0;
    return -this.gridMatrix
      .flat()
      .filter((v) => v > 0)
      .reduce((sum, v) => {
        const p = v / total;
        return sum + p * Math.log2(p);
      }, 0);
  }

  /** Measures how many cells are empty */
  sparsity(): number {
    const zeroCount = this.gridMatrix.flat().filter((v) => v === 0).length;
    return zeroCount / (this.m * this.n);
  }

  /** Measures the proportion of occupied cells */
  occupancyRatio(): number {
    return 1 - this.sparsity();
  }

  /** Finds the highest movement occurrence in a single cell */
  maxDensity(): number {
    return Math.max(...this.gridMatrix.flat());
  }

  /** Finds the lowest nonzero movement occurrence */
  minDensity(): number {
    return Math.min(...this.gridMatrix.flat().filter((v) => v > 0), 0);
  }

  /** Computes Gini coefficient for movement distribution inequality */
  giniCoefficient(): number {
    const values = this.gridMatrix.flat().sort((a, b) => a - b);
    const n = values.length;
    const mean = this.meanDensity();
    return (
      ((2 / n) * values.reduce((sum, v, i) => sum + (i + 1) * v, 0)) /
        (n * mean) -
      (n + 1) / n
    );
  }

  /** Moran’s I measures spatial clustering of movements */
  moransI(): number {
    const mean = this.meanDensity();
    const total = this.gridMatrix.flat().reduce((sum, v) => sum + v, 0);
    if (total === 0) return 0;
    let numerator = 0,
      denominator = 0;
    for (let i = 0; i < this.m; i++) {
      for (let j = 0; j < this.n; j++) {
        if (this.gridMatrix[i][j] > 0) {
          if (i > 0)
            numerator +=
              (this.gridMatrix[i][j] - mean) *
              (this.gridMatrix[i - 1][j] - mean);
          if (j > 0)
            numerator +=
              (this.gridMatrix[i][j] - mean) *
              (this.gridMatrix[i][j - 1] - mean);
        }
        denominator += Math.pow(this.gridMatrix[i][j] - mean, 2);
      }
    }
    return numerator / denominator;
  }

  /** Generates a well-readable string summary of all computed metrics */
  generateMetricsReport(): string {
    return (
      `Heatmap Metrics Report:\n` +
      `- Mean Density: ${this.meanDensity().toFixed(2)}\n` +
      `- Variance: ${this.variance().toFixed(2)}\n` +
      `- Standard Deviation: ${this.standardDeviation().toFixed(2)}\n` +
      `- Entropy: ${this.entropy().toFixed(2)}\n` +
      `- Sparsity: ${(this.sparsity() * 100).toFixed(2)}%\n` +
      `- Occupancy Ratio: ${(this.occupancyRatio() * 100).toFixed(2)}%\n` +
      `- Max Density: ${this.maxDensity()}\n` +
      `- Min Density: ${this.minDensity()}\n` +
      `- Gini Coefficient: ${this.giniCoefficient().toFixed(2)}\n` +
      `- Moran’s I: ${this.moransI().toFixed(2)}\n` +
      `- toolLeft Tool Total Distance: ${this.totalDistance.toolLeft.toFixed(2)} cells\n` +
      `- toolRight Tool Total Distance: ${this.totalDistance.toolRight.toFixed(2)} cells\n`
    );
  }
}
